import {
    collection,
    doc,
    getDoc,
    getDocs,
    query,
    where,
    updateDoc,
    deleteDoc,
    or,
    addDoc,
    writeBatch,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getStorage, ref, deleteObject } from 'firebase/storage';
import { db } from '../firebase';
import { AppStore } from '../stores/AppStore';
import { OnboardingStore } from '../stores/OnboardingStore';
import logOperation from './LogReads';

export const updateCredits = async (user) => {
    try {
        const docRef = doc(db, 'users', user);
        const docSnap = await getDoc(docRef);
        // console.log('docRef', docRef);
        // console.log('docSnap', docSnap);
        let credits;

        if (docSnap.exists()) {
            // console.log('user doc found');
            const data = docSnap.data();
            // console.log('data', data);
            credits = data.credits;
        } else {
            console.error('user doc not found');
        }

        AppStore.update((s) => {
            s.userDoc = { ...s.userDoc, credits: credits };
        });
    } catch (error) {
        // Handle error here
        console.error(error);
    }
};
// Outcome of the sign-in gate in App.jsx. These are three distinct states, not a
// boolean: sign-ups are closed, so a missing user doc means an account we must
// reject and delete — and a *failed read* must never be mistaken for one, or a
// momentary network fault would delete an existing user's account.
export const USER_DOC_OK = 'ok'; // existing account, loaded
export const USER_DOC_NONE = 'none'; // confirmed: no account exists
export const USER_DOC_ERROR = 'error'; // couldn't tell — never destructive
export const USER_DOC_ENDED = 'ended'; // access period is over (wind-down cutoff)

export const getSetUserDoc = async (user) => {
    let docSnap;
    try {
        const docRef = doc(db, 'users', user.uid);
        docSnap = await getDoc(docRef);
        await logOperation(user, 'read', `users/${user.uid}`);
        // console.log('userid', user.uid);
    } catch (error) {
        console.error(error);
        // Security rules deny the read once accessEndsAt has passed. That is the
        // wind-down cutoff, not a fault, and it needs its own message — telling
        // someone their access ended is very different from "try again".
        if (error?.code === 'permission-denied') return USER_DOC_ENDED;
        // Any other failure: we do not know whether this account exists.
        // Report that plainly — the caller must not create or delete anything.
        return USER_DOC_ERROR;
    }

    if (!docSnap.exists()) {
        // Sign-ups are closed — Passed.ai is no longer accepting new users.
        // Do NOT create an account here. The caller (the auth gate in App.jsx)
        // rejects and removes the just-authenticated account.
        return USER_DOC_NONE;
    }

    // Existing user — load their account as normal.
    const data = docSnap.data();
    const id = docSnap.id;
    const userDoc = { ...data, id };
    if (data?.institutionId) {
        try {
            const institutionRef = doc(db, 'institutions', data.institutionId);
            const institutionSnap = await getDoc(institutionRef);
            const institutionData = institutionSnap.data();
            userDoc.institutionInfo = institutionData;
            AppStore.update((s) => {
                s.user = user;
                s.userDoc = { ...s.userDoc, institutionInfo: institutionData, ready: true };
            });
        } catch (error) {
            // The account is valid regardless — sign them in without the
            // institution details rather than failing the whole login.
            console.error(error);
        }
    }
    AppStore.update((s) => {
        s.user = user;
        s.userDoc = { ...userDoc, ready: data.institutionId || data.ready ? true : false };
    });
    return USER_DOC_OK;
};

export const activeSub = async (user) => {
    try {
        const subRef = collection(db, 'users', user.uid, 'subscriptions');
        const activeSubRef = query(subRef, where('status', '==', 'active'));
        const activeSubSnap = await getDocs(activeSubRef);
        await logOperation(user, 'read', `users/${user.uid}/subscriptions`);
        // console.log('activeSubSnap', activeSubSnap);
        if (activeSubSnap.docs.length > 0) {
            // console.log('active subscriptions found');
            return true;
        } else {
            return false;
        }
    } catch (error) {
        // Handle error here
        console.error('error', error);
    }
};

export const getSubscriptions = async (user) => {
    // console.log('getting subscriptions');
    try {
        const subRef = collection(db, 'users', user.uid, 'subscriptions');
        const activeSubRef = query(
            subRef,
            or(where('status', '==', 'active'), where('status', '==', 'trialing'))
        );
        const activeSubSnap = await getDocs(activeSubRef);
        await logOperation(user, 'read', `users/${user.uid}/subscriptions`);
        const subsArray = [];
        // console.log('activeSubSnap', activeSubSnap);
        if (activeSubSnap.docs.length > 0) {
            // console.log('active subscriptions found');
            activeSubSnap.docs.forEach((doc) => {
                const data = doc.data();
                const id = doc.id;
                subsArray.push({ ...data, id });
            });
            //set user to active
            const docRef = doc(db, 'users', user.uid);
            await updateDoc(docRef, {
                active: true,
            });

            AppStore.update((s) => {
                s.userDoc = {
                    ...s.userDoc,
                    subscriptions: subsArray,
                    active: true,
                };
            });
            OnboardingStore.update((s) => {
                s.step = 3;
            });
        } else {
            // console.log('no active subscriptions found');
            AppStore.update((s) => {
                s.userDoc = {
                    ...s.userDoc,
                    subscriptions: [],
                    active: false,
                };
            });
            const docRef = doc(db, 'users', user.uid);
            await updateDoc(docRef, {
                active: false,
            });
        }
    } catch (error) {
        // Handle error here
        console.error('getSubscriptions error', error);
    }
};

export const getPlans = async () => {
    try {
        const plansQuery = query(collection(db, 'plans'), where('active', '==', true));
        const querySnapshot = await getDocs(plansQuery);
        const plansArray = [];

        for (const doc of querySnapshot.docs) {
            const priceSnap = await getDocs(collection(doc.ref, 'prices'));
            const prices = [];

            for (const priceDoc of priceSnap.docs) {
                const pData = priceDoc.data();
                const pId = priceDoc.id;
                if (pData.active) {
                    prices.push({ ...pData, id: pId });
                }
            }

            plansArray.push({ ...doc.data(), id: doc.id, prices });
        }

        AppStore.update((s) => {
            s.plans = plansArray;
        });
    } catch (error) {
        // Handle error here
        console.error(error);
    }
};

export const getPlan = async (id) => {
    //get doc matching id
    const planRef = doc(db, 'plans', id);
    const planSnap = await getDoc(planRef);
    const planData = planSnap.data();
    const planId = planSnap.id;
    const plan = { ...planData, id: planId };

    //get prices
    const priceRef = collection(db, 'plans', id, 'prices');
    const priceSnap = await getDocs(priceRef);
    const prices = [];
    priceSnap.docs.forEach((doc) => {
        const data = doc.data();
        const id = doc.id;
        prices.push({ ...data, id });
    });
    plan.prices = prices;

    return plan;
};

export const getSub = async (userId) => {
    const userRef = doc(db, 'users', userId);
    const subscriptionQuery = query(
        collection(userRef, 'subscriptions'),
        or(where('status', '==', 'active'), where('status', '==', 'trialing'))
    );

    const querySnapshot = await getDocs(subscriptionQuery);

    if (!querySnapshot.empty) {
        const activeSubscription = querySnapshot.docs[0];
        return activeSubscription.id;
    } else {
        console.log('No active subscriptions found for this user.');
    }
};

export const getSubCollectionData = async (userId, subCollectionName, store) => {
    // console.log('getting subcollection data', userId, subCollectionName);
    const userRef = doc(db, 'users', userId);
    const subCollectionQuery = query(collection(userRef, subCollectionName));
    const querySnapshot = await getDocs(subCollectionQuery);
    await logOperation(userId, 'read', `users/${userId}/${subCollectionName}`);
    const data = [];

    if (!querySnapshot.empty) {
        querySnapshot.forEach((doc) => {
            const docData = doc.data();
            const id = doc.id;
            data.push({ ...docData, id });
        });
    } else {
        // console.log(`No ${subCollectionName} found for this user.`);
    }
    // console.log('data', data);
    store.update((s) => {
        s[subCollectionName] = data;
    });

    return data;
};
export const addDocToSubCollection = async (userId, subCollectionName, data, store) => {
    const userRef = doc(db, 'users', userId);
    const subCollectionRef = collection(userRef, subCollectionName);

    // Check if a document with the same identifier already exists, but only for 'students' and 'courses' subcollections
    if (['courses', 'students'].includes(subCollectionName)) {
        const queryRef = query(subCollectionRef, where('identifier', '==', data.identifier));
        const querySnapshot = await getDocs(queryRef);

        if (!querySnapshot.empty) {
            throw new Error('A document with the same identifier already exists.');
        }
    }

    const docRef = await addDoc(subCollectionRef, data);
    console.log('docRef', docRef);

    store.update((s) => {
        const updatedData = [...s[subCollectionName], { ...data, id: docRef.id }];
        return { ...s, [subCollectionName]: updatedData };
    });

    return docRef.id;
};

export const editDocInSubCollection = async (userId, subCollectionName, docId, newData, store) => {
    const userRef = doc(db, 'users', userId);
    const docRef = doc(userRef, subCollectionName, docId);
    await updateDoc(docRef, newData);

    store.update((s) => {
        const updatedData = s[subCollectionName].map((item) =>
            item.id === docId ? { ...item, ...newData } : item
        );
        return { ...s, [subCollectionName]: updatedData };
    });

    if (subCollectionName === 'courses' || subCollectionName === 'students') {
        getSubCollectionData(userId, 'scans');
    }
};
export const deleteDocFromSubCollection = async (userId, subCollectionName, docId, store) => {
    const userRef = doc(db, 'users', userId);
    const docRef = doc(userRef, subCollectionName, docId);
    await deleteDoc(docRef);

    if (store) {
        store.update((s) => {
            const updatedData = s[subCollectionName].filter((item) => item.id !== docId);
            return { ...s, [subCollectionName]: updatedData };
        });
    }
};

export const shareAssignment = async (userId, subCollectionName, docId, store, students) => {
    // console.log('sharing assignment', userId, subCollectionName, docId, store, students);
    const docRef = doc(db, 'users', userId, subCollectionName, docId);
    // console.log('docRef', docRef);
    const studentsRef = collection(docRef, 'students');

    // Create a new batch using writeBatch instead of db.batch
    const batch = writeBatch(db);

    students.forEach((student) => {
        const studentRef = doc(studentsRef, student);
        batch.set(studentRef, { email: student });
    });

    try {
        await batch.commit();
        await updateDoc(docRef, { shared: true });
    } catch (error) {
        console.error('Error sharing assignment:', error);
        throw error;
    }

    if (store) {
        store.update((s) => {
            const updatedData = s[subCollectionName].filter((item) => item.id !== docId);
            return { ...s, [subCollectionName]: updatedData };
        });
    }
};
export const deleteFromFirestore = async (userId) => {
    const userDocRef = doc(db, 'users', userId);

    const deleteCollectionDocs = async (collectionPath) => {
        const collectionRef = collection(db, collectionPath);
        const docsSnapshot = await getDocs(collectionRef);
        docsSnapshot.forEach((doc) => deleteDoc(doc.ref));

        if (!docsSnapshot.empty) {
            docsSnapshot.forEach((doc) => deleteDoc(doc.ref));
        }
    };

    // List all the subcollections
    const collectionsToDelete = [
        'checkout_sessions',
        'marketingEmails',
        'payments',
        'subscriptions',
    ];

    for (const collectionName of collectionsToDelete) {
        await deleteCollectionDocs(`users/${userId}/${collectionName}`);
    }

    // Delete the main user document after all its subcollections have been deleted
    await deleteDoc(userDocRef);
};

export const deleteFromAuth = async () => {
    const auth = getAuth();
    const user = auth.currentUser;

    if (user) {
        try {
            await user.delete();
            console.log('User deleted from Firebase Auth');
        } catch (error) {
            console.error('Error deleting user from Firebase Auth:', error);
        }
    } else {
        console.log('No user currently logged in');
    }
};

export const deleteFromFireBase = async (userId) => {
    const storage = getStorage();

    const userRef = ref(storage, `users/${userId}`);
    await deleteObject(userRef);
};

export const getScanHistory = async (user) => {
    const scansRef = collection(db, 'users', user.uid, 'scans');
    // ✅ fetch ALL scans (no filter)
    const querySnapshot = await getDocs(scansRef);

    const prevScans = querySnapshot.docs.map((doc) => {
        const data = doc.data();

        // Try new schema first, fallback to old
        const aiScore =
            data?.scanData?.fullScanData?.scan_results?.aiResults?.fake ??
            data?.fullScanData?.scan_results?.aiResults?.fake ??
            null;

        const originalScore =
            data?.scanData?.fullScanData?.scan_results?.aiResults?.real ??
            data?.fullScanData?.scan_results?.aiResults?.real ??
            null;

        const plagiarismScore =
            data?.scanData?.fullScanData?.scan_results?.plagResults?.documentScore ??
            data?.fullScanData?.scan_results?.plagResults?.documentScore ??
            null;

        const scanType =
            data?.scanData?.fullScanData?.scan_results?.scan?.scan_type ??
            data?.fullScanData?.scan_results?.scan?.scan_type ??
            null;

        return {
            ...data,
            id: doc.id,
            aiScore,
            originalScore,
            plagiarismScore: scanType === 1 ? 'N/A' : plagiarismScore,
        };
    });

    // De-dupe by doc id (though Firestore shouldn’t return duplicates)
    const seen = new Set();
    return prevScans.filter((el) => {
        if (seen.has(el.id)) return false;
        seen.add(el.id);
        return true;
    });
};

export const getOperations = async () => {
    const operationsRef = collection(db, 'operationLogs');
    const q = query(operationsRef);
    const operations = [];
    const querySnapshot = await getDocs(q);
    querySnapshot.forEach((doc) => {
        const data = doc.data();
        operations.push({ ...data, id: doc.id });
    });

    return operations;
};
